import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import { workoutAnalysisChartData } from "../../../../providers/dataPlatformReportTypes";
import ColoredDot from "../shared/ColoredDot";

/**
 * Section 3 · Sub 3.2 — Nutrition plan. Visual reference: Figma node
 * 14541:69387.
 *
 * Two side-by-side cards (Workout days / Not workout days). Each card body
 * shows Intake vs Burn, then a macro donut + legend + per-macro grams/percent.
 * The bottom of each card is a colored strip showing the calorie balance:
 * green "Surplus +N kcal" when intake ≥ burn, red "Deficit −N kcal" otherwise.
 *
 * Data: `data.nutritionPlan.{workout, nonWorkout}AnalysisChartData`. Burn is
 * the sum of `rmrValue + dailyActivityValue + workoutValue`. Macros come from
 * `pieData[]` ({ label, valueInGram }).
 */
/**
 * Macro colors — mirrors the platform's `getBiomarkerCategoryColor(_, _,
 * "nutrition")` mapping so the consultation matches the Nutrition Plan
 * tab in `src/modules/platform/components/programming/`:
 *   Protein → W[60] (yellow), Carbs → P[60] (blue), Fat → S[60] (green).
 */
const macroColor = (theme: Theme, label: string): string => {
  switch (label?.toLowerCase()) {
    case "protein":
      return theme.colorTokens.W[60];
    case "carbs":
      return theme.colorTokens.P[60];
    case "fats":
    case "fat":
      return theme.colorTokens.S[60];
    default:
      return theme.colorTokens.N[60];
  }
};

/**
 * Calories per gram by macro. Mirrors the platform's NutritionBars math
 * (protein/carbs = 4 kcal/g, fat = 9 kcal/g) so percentages match the report
 * screen and the Figma example (150gr / 225gr / 56gr → 30% / 45% / 25%).
 */
const kcalPerGram = (label: string): number => {
  const k = label?.toLowerCase();
  return k === "fat" || k === "fats" ? 9 : 4;
};

/**
 * 92×92 donut chart with a 7px ring thickness (Figma node 14541:69069).
 * Slice fractions are kcal-based (not gram-based) so the chart matches the
 * platform's NutritionBars math and the Figma percentages (protein/carbs ×
 * 4, fat × 9).
 */
const DONUT_SIZE = 92;
const DONUT_OUTER_R = 46;
const DONUT_INNER_R = 39; // 46 − 7 = 7px ring thickness

const MacroPie: React.FC<{
  pieData: { label: string; valueInGram: number }[];
}> = ({ pieData }) => {
  const theme = useTheme();
  const kcalSlices = pieData.map((p) => ({
    label: p.label,
    kcal: (p.valueInGram || 0) * kcalPerGram(p.label),
  }));
  const total = kcalSlices.reduce((acc, p) => acc + p.kcal, 0) || 1;
  const cx = DONUT_SIZE / 2;
  const cy = DONUT_SIZE / 2;
  let cursor = 0;
  return (
    <Box
      component="svg"
      sx={styles.pie}
      viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
    >
      {kcalSlices.map((slice) => {
        const fraction = slice.kcal / total;
        const startAngle = cursor * 2 * Math.PI - Math.PI / 2;
        cursor += fraction;
        const endAngle = cursor * 2 * Math.PI - Math.PI / 2;
        const largeArc = fraction > 0.5 ? 1 : 0;
        const x1 = cx + DONUT_OUTER_R * Math.cos(startAngle);
        const y1 = cy + DONUT_OUTER_R * Math.sin(startAngle);
        const x2 = cx + DONUT_OUTER_R * Math.cos(endAngle);
        const y2 = cy + DONUT_OUTER_R * Math.sin(endAngle);
        return (
          <path
            key={slice.label}
            d={`M ${cx} ${cy} L ${x1} ${y1} A ${DONUT_OUTER_R} ${DONUT_OUTER_R} 0 ${largeArc} 1 ${x2} ${y2} Z`}
            fill={macroColor(theme, slice.label)}
          />
        );
      })}
      <circle
        cx={cx}
        cy={cy}
        r={DONUT_INNER_R}
        fill={theme.colors_ds.NEUTRAL_BG_MAIN}
      />
    </Box>
  );
};

const StatColumn: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.stat}>
      <Box sx={styles.statLabel(theme)}>{label}</Box>
      <Box sx={styles.statValue(theme)}>{value.toLocaleString()}</Box>
      <Box sx={styles.statUnit(theme)}>kcal</Box>
    </Box>
  );
};

const NutritionCard: React.FC<{
  title: string;
  data: workoutAnalysisChartData | undefined;
}> = ({ title, data }) => {
  const theme: Theme = useTheme();
  if (!data) {
    return (
      <Box sx={styles.card(theme)}>
        <Box sx={styles.body}>
          <Box sx={styles.cardTitle(theme)}>{title}</Box>
          <Box sx={styles.subtle(theme)}>No data available.</Box>
        </Box>
      </Box>
    );
  }

  const intake = data.caloricIntakeValue ?? 0;
  const burn =
    (data.rmrValue ?? 0) +
    (data.dailyActivityValue ?? 0) +
    (data.workoutValue ?? 0);
  const balance = intake - burn;
  const macros = data.pieData ?? [];
  // Kcal-based total to drive percentages — matches the platform's
  // NutritionBars math (fat × 9, protein/carbs × 4).
  const totalKcal =
    macros.reduce(
      (a, m) => a + (m.valueInGram || 0) * kcalPerGram(m.label),
      0
    ) || 1;
  const isSurplus = balance >= 0;

  return (
    <Box sx={styles.card(theme)}>
      <Box sx={styles.body}>
        <Box sx={styles.cardTitle(theme)}>{title}</Box>

        <Box sx={styles.divider(theme)} />

        <Box sx={styles.intakeBurnRow}>
          <StatColumn label="Intake" value={intake} />
          <Box sx={styles.vs(theme)}>VS</Box>
          <StatColumn label="Burn" value={burn} />
        </Box>

        <Box sx={styles.divider(theme)} />

        <Box sx={styles.macrosRow}>
          <Box sx={styles.macrosLeft}>
            <MacroPie pieData={macros} />
            <Box sx={styles.macrosLegend}>
              {macros.map((m) => (
                <Box key={m.label} sx={styles.legendRow(theme)}>
                  <ColoredDot color={macroColor(theme, m.label)} />
                  <Box component="span" sx={styles.legendLabel(theme)}>
                    {m.label}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          {/* CSS grid so the three columns (gr / kcal / %) line up across
              all three macro rows. Each column sizes to its widest cell,
              and cell content is right-aligned via `justifyItems: end`. */}
          <Box sx={styles.macrosValues}>
            {macros.map((m) => {
              const kcal = (m.valueInGram || 0) * kcalPerGram(m.label);
              const pct = Math.round((kcal / totalKcal) * 100);
              return (
                <React.Fragment key={m.label}>
                  <Box component="span" sx={styles.valueGrams(theme)}>
                    {m.valueInGram} gr
                  </Box>
                  {/* Kcal — addition over the Figma so the user sees the
                      energy contribution alongside the gram weight. */}
                  <Box component="span" sx={styles.valueKcal(theme)}>
                    {kcal.toLocaleString()} kcal
                  </Box>
                  <Box component="span" sx={styles.valuePct(theme)}>
                    {pct}%
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.balanceFooter(theme, isSurplus)}>
        <Box component="span" sx={styles.balanceLabel(theme, isSurplus)}>
          {isSurplus ? "Surplus" : "Deficit"}
        </Box>
        <Box component="span" sx={styles.balanceValue(theme, isSurplus)}>
          {isSurplus ? "+" : ""}
          {balance.toLocaleString()} kcal
        </Box>
      </Box>
    </Box>
  );
};

const NutritionPanel: React.FC = () => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();
  const plan = data.nutritionPlan;
  if (!plan) {
    return (
      <Box component="section" sx={styles.section}>
        <Box component="h2" sx={styles.title(theme)}>
          Nutrition plan
        </Box>
        <Box sx={styles.subtle(theme)}>
          No nutrition plan is available for this client yet.
        </Box>
      </Box>
    );
  }

  return (
    <Box component="section" sx={styles.section}>
      <Box component="h2" sx={styles.title(theme)}>
        Nutrition plan
      </Box>
      <Box sx={styles.row}>
        <NutritionCard
          title="Workout days"
          data={plan.workoutAnalysisChartData}
        />
        <NutritionCard
          title="Not workout days"
          data={plan.nonWorkoutAnalysisChartData}
        />
      </Box>
    </Box>
  );
};

export default NutritionPanel;

const styles = {
  section: {
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  title: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.6,
    color: theme.colorTokens.N[100],
    width: "100%",
  }),
  subtle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    width: "100%",
  },
  card: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    minWidth: 0,
  }),
  body: {
    padding: "16px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "16px",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  cardTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    width: "100%",
  }),
  divider: (theme: Theme) => ({
    width: "100%",
    height: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  intakeBurnRow: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
    justifyContent: "center",
  },
  stat: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
  },
  statLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap" as const,
  }),
  statValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    lineHeight: 1.4,
    letterSpacing: "-0.36px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    whiteSpace: "nowrap" as const,
  }),
  statUnit: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  vs: (theme: Theme) => ({
    width: "41px",
    textAlign: "center" as const,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  macrosRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: "16px",
  },
  macrosLeft: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
  },
  pie: {
    width: "92px",
    height: "92px",
    flexShrink: 0,
  },
  macrosLegend: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: "12px",
    width: "69px",
  },
  legendRow: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  legendLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    whiteSpace: "nowrap" as const,
  }),
  macrosValues: {
    display: "grid",
    gridTemplateColumns: "auto auto auto",
    rowGap: "12px",
    columnGap: "8px",
    alignItems: "center",
    justifyItems: "end" as const,
  },
  valueGrams: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    whiteSpace: "nowrap" as const,
  }),
  valueKcal: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap" as const,
  }),
  valuePct: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap" as const,
  }),
  balanceFooter: (theme: Theme, isSurplus: boolean) => ({
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    padding: "12px",
    backgroundColor: isSurplus
      ? theme.colorTokens.S[10]
      : theme.colorTokens.D[10],
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    boxSizing: "border-box" as const,
  }),
  balanceLabel: (theme: Theme, isSurplus: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: isSurplus ? theme.colorTokens.S[80] : theme.colorTokens.D[80],
  }),
  balanceValue: (theme: Theme, isSurplus: boolean) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: isSurplus ? theme.colorTokens.S[80] : theme.colorTokens.D[80],
  }),
};
